import {useReports} from '@sail/observability';
import {useMemo} from 'react';
import {useNavigationConfig} from 'src/internal/config/NavigationConfigContext';
import urlToRoutes from 'src/internal/urlToRoutes';
import useHref from 'src/public/useHref/useHref';

import type {RouteNames, RouteResult} from 'src/types';

/**
 * React hook to handle all location routes.
 *
 * Similar to how `useRoute` operates, but returns all defined routes that
 * match against the current URL. Within the route map you may actually have
 * more than one URL that matches due to defining certain globs or catch-all
 * routes. For instance, with the following route map:
 *
 * ```tsx
 * {
 *   ...prefix('prefix', '/foo', {
 *     FOO_NEW: '/new',
 *     FOO_ID: '/:id',
 *   }),
 *
 *   CATCH_ALL: '/:glob*',
 * }
 * ```
 *
 * A route `/foo/new` would match `FOO_NEW` as the first one, but also `FOO_ID`
 * (`id` is `'new'`) and `CATCH_ALL` (`glob` is `['foo', 'new']`).
 *
 * @see https://trailhead.corp.stripe.com/docs/sail-navigation/api/useRoutes
 */
export default function useRoutes(): {
  [name in RouteNames]?: RouteResult<name>;
} {
  const [href] = useHref();
  const config = useNavigationConfig();
  const reports = useReports();

  const currentRoutes = useMemo(() => {
    try {
      return urlToRoutes(config, href);
    } catch (error) {
      if (error instanceof Error) {
        reports.error(error.message, {
          extras: {
            tag: {namespace: 'navigation'},
          },
        });
      } else {
        reports.error(`An unknown error occurred ${error}`, {
          extras: {
            tag: {namespace: 'navigation'},
            error,
          },
        });
      }

      throw error;
    }
  }, [config, href, reports]);

  return currentRoutes;
}
